import { useCallback, useMemo, useRef, useState } from "react";
import * as s from "../AlertViewStyled";
import { convertVideoStartMs, getRecorderTimeFormat } from "utils/timeUtil";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import {
  AlertTraceList,
  Assignee,
} from "api/interfaces/alertInterface.interface";
import Input from "components/atoms/input/Input";
import PrimaryButton from "components/atoms/buttons/PrimaryButton";
import { useLocation } from "react-router-dom";
import { useMutation } from "react-query";
import {
  getAlertViewUserList,
  postAlertAssign,
  postAlertComment,
} from "api/alertAPI";
import { useQueryClient } from "react-query";
import { useQuery } from "react-query";
import { SearchUserAllList } from "api/interfaces/userInterface.interface";
import { Option } from "utils/options";
import useApiError from "hook/useApiError";
import { CustomError } from "api/interfaces/commonInterface.interface";
import { Text } from "components/atoms/text/Text";
import { COLORS, statusToColor } from "styles/colors";
import SelectComponent, {
  SelectVariant,
} from "components/atoms/select/SelectComponent";
import { TableColumnsType } from "antd";
import { AlertViewTable } from "components/atoms/table/AntdTable";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import SendAssignee from "./SendAssignee";
import { toTrim } from "utils/functions";
interface Props {
  data: AlertTraceList[];
  alertHistoryId: string;
  recorderId: string;
  assignee: Assignee | undefined;
  isClosed: boolean;
  alertViewRefetch: () => void;
}

const TraceList = (props: Props) => {
  const location = useLocation();
  const { handleErrorNotify, lang, notifySuccess, notifyError } = useApiError();

  const queryClient = useQueryClient();
  const isMap = location.pathname.includes("map");
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const tracelistStyled = s.TraceListStyled;

  // assign
  const [assign, setAssign] = useState<{ email?: string; message: string }>({
    email: undefined,
    message: "",
  });

  // trace list comment
  const [comment, setComment] = useState<string>("");
  const usersQuery = useQuery(
    ["getAllUser", selectedAccount.accountId, props.recorderId],
    () =>
      getAlertViewUserList({
        accountId: selectedAccount.accountId,
        recorderId: props.recorderId,
      }),
    {
      retry: 3,
    }
  );
  const users: Option[] = useMemo(
    () =>
      (usersQuery.data?.result ?? []).map((r: SearchUserAllList) => ({
        label: r.name,
        value: r.email,
      })).sort((a: Option, b: Option) => a.label.localeCompare(b.label)),
    [usersQuery.data]
  );

  const columns: TableColumnsType<AlertTraceList> = useMemo(
    () => [
      {
        title: "Action",
        render: (value, row) => (
          <span style={{ color: statusToColor(row.action) }}>{row.action}</span>
        ),

        width: 80,
      },
      {
        title: "Date & Time",
        // render: (row) => changeTimeStampFormat(row.eventDate),
        render: (value, row) =>
          row.createdAt !== undefined ? (
            <div style={{ minWidth: "80px" }}>
              {getRecorderTimeFormat(
                row.createdAt,
                selectedAccount,
                row.timezone,
                true
              )}
            </div>
          ) : (
            ""
          ),
      },
      {
        title: "User",
        render: (value, row) => row.username,
      },
      {
        title: "Note",
        render: (value, row) => {
          const additionalInfoArr = (): string[] => {
            return (row.additionalInfo || "")
              .split(",")
              .map((info) => toTrim(info))
              .filter((info) => !isNaN(Number(info)) && info !== "");
          };

          return (
            <tracelistStyled.NoteWrapper>
              {row.comment}
              {additionalInfoArr().length > 0 && (
                <tracelistStyled.NoteList>
                  {additionalInfoArr().map((unixTime: string) => {
                    return (
                      <li>
                        •{" "}
                        {unixTime &&
                          convertVideoStartMs(
                            String(Number(unixTime) * 1000),
                            row.timezone,
                            selectedAccount
                          )}
                      </li>
                    );
                  })}
                </tracelistStyled.NoteList>
              )}
            </tracelistStyled.NoteWrapper>
          );
        },
        ellipsis: true,
        width: "50%",
      },
    ],

    []
  );

  const mutationAssign = useMutation(postAlertAssign, {
    onSuccess: () => {
      notifySuccess(lang.label.alert.notify.assigned.success);
      setAssign({
        email: undefined,
        message: "",
      });
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.alert.notify.assign.fail);
    },
  });
  const mutationInsertComment = useMutation(postAlertComment, {
    onSuccess: () => {
      notifySuccess(lang.label.videoshare.notify.commentAdd.success);
      setComment("");
      queryClient.invalidateQueries("alertTraceList");
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.videoshare.notify.commentAdd.fail);
    },
  });

  const onAssign = useCallback(() => {
    mutationAssign.mutate({
      accountId: selectedAccount.accountId,
      recorderId: props.recorderId,
      payload: {
        email: assign.email ?? "",
        message: toTrim(assign.message),
      },
    });
  }, [assign, selectedAccount.accountId, props.recorderId, mutationAssign]);

  const onComment = useCallback(() => {
    mutationInsertComment.mutate({
      accountId: selectedAccount.accountId,
      recorderId: props.recorderId,
      payload: {
        note: toTrim(comment),
      },
    });
  }, [
    comment,
    mutationInsertComment,
    selectedAccount.accountId,
    props.recorderId,
  ]);

  const onChange = useCallback(
    (e: any) => {
      let {
        target: { name, value },
      } = e;

      if (name === "comment") {
        setComment(value);
      } else {
        setAssign({
          ...assign,
          [name]: value,
        });
      }
    },
    [comment, assign]
  );

  const onChangeAssignee = useCallback(
    (value: string) => {
      setAssign({
        ...assign,
        email: value,
      });
    },
    [assign]
  );

  return (
    <>
      <tracelistStyled.TraceListWrapper isMap={isMap}>
        <tracelistStyled.AssignWrapper isMap={isMap}>
          <RBACWrapper requiredPermissions="manageAlertRules">
            <li className="technician column-box">
              <div className="subtitle">Assign to a Technician</div>
              <tracelistStyled.AlertTraceInputWrapper>
                <div className="select-box assign">
                  <SelectComponent
                    mydwVariant={SelectVariant.ALERT}
                    value={assign.email}
                    options={users}
                    onChange={onChangeAssignee}
                    disabled={props.isClosed}
                    placeholder="Select"
                    loading={usersQuery.isFetching}
                    showSearch={true}
                  />
                </div>
                <Input
                  placeholder="Enter a message to send to the assigned technician."
                  name="message"
                  onChange={onChange}
                  value={assign.message}
                  disabled={props.isClosed}
                  data-testid="alert-view-components-message-change"
                />

                <PrimaryButton
                  label="Assign"
                  onClickBtn={onAssign}
                  disabled={props.isClosed}
                  data-testid="alert-view-components-primary-button-assign"
                />
              </tracelistStyled.AlertTraceInputWrapper>
            </li>
          </RBACWrapper>

          {((isMap && props.assignee !== undefined) || !isMap) && (
            <li className="assignto column-box">
              <div className="subtitle">Assignee</div>

              <tracelistStyled.AssignBox>
                <SendAssignee
                  recorderId={props.recorderId}
                  assignee={props.assignee?.assignee ?? ""}
                  assigneeId={props.assignee?.assigneeId ?? ""}
                  assigneePhone={props.assignee?.assigneePhone || ""}
                  assigneeMail={props.assignee?.assigneeMail ?? ""}
                  notifySms={props.assignee?.notifySms ?? false}
                  refetch={() => props.alertViewRefetch()}
                  placeholder="<NONE>"
                />
              </tracelistStyled.AssignBox>
            </li>
          )}
        </tracelistStyled.AssignWrapper>
        <tracelistStyled.TraceList>
          <tracelistStyled.TraceTitle isMap={isMap}>
            <div className="subtitle">Alert Trace List</div>
            {!props.isClosed && (
              <RBACWrapper requiredPermissions="viewAlerts">
                <tracelistStyled.AlertTraceInputWrapper>
                  <Input
                    placeholder="Add a comment"
                    name="comment"
                    value={comment}
                    onChange={onChange}
                    data-testid="alert-view-components-comment-change"
                  />

                  <PrimaryButton
                    label="Comment"
                    onClickBtn={onComment}
                    data-testid="alert-view-components-primary-button-comment"
                  />
                </tracelistStyled.AlertTraceInputWrapper>
              </RBACWrapper>
            )}
          </tracelistStyled.TraceTitle>
          {isMap ? (
            <tracelistStyled.AlertTraceItemWrapper>
              {props.data.map((trace: AlertTraceList, idx: number) => {
                return (
                  <li key={`trace-item-${idx}`}>
                    <div className="title-wrapper">
                      <Text fontSize={15}>{trace.username}</Text>
                      <Text fontSize={14} color={COLORS.LIGHTGRAY}>
                        {trace.createdAt !== undefined &&
                          getRecorderTimeFormat(
                            trace.createdAt,
                            selectedAccount,
                            trace.timezone,
                            true
                          )}
                      </Text>
                    </div>
                    <div className="status">
                      Status:{" "}
                      <Text color={statusToColor(trace.action)} fontSize={14}>
                        {trace.action}
                      </Text>
                    </div>
                    <div className="trace-comment">
                      Comment: <span className="comment">{trace.comment}</span>
                    </div>
                  </li>
                );
              })}
            </tracelistStyled.AlertTraceItemWrapper>
          ) : (
            <AlertViewTable columns={columns} dataSource={props.data} />
          )}
        </tracelistStyled.TraceList>
      </tracelistStyled.TraceListWrapper>
    </>
  );
};

export default TraceList;
